import * as Sentry from '@sentry/react'

/**
 * Error message/type patterns that are non-actionable browser noise and must be
 * dropped before reaching Sentry to avoid exhausting our quota.
 *
 * Keep in sync with the `ignoreErrors` array in `Sentry.init()` below. We filter
 * in `beforeSend` as well because `ignoreErrors` only matches the top-level
 * message and can miss these errors when they arrive as nested exception values
 * or unhandled promise rejections.
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // IndexedDB connection lost - typically caused by browser/tab refresh, device
  // sleep, or storage quota issues. Not actionable; generated 27K+ events.
  /Connection to Indexed Database server lost/i,
  // WebSocket connection timeout - high-volume reconnection noise (100K+ events)
  // emitted via captureMessage('WebSocket: connection_timeout').
  /WebSocket: connection_timeout/i,
]

function isIgnoredError(event: Sentry.ErrorEvent): boolean {
  const candidates: string[] = []

  if (typeof event.message === 'string') {
    candidates.push(event.message)
  }

  for (const value of event.exception?.values ?? []) {
    if (value?.value) {
      candidates.push(value.value)
    }
    if (value?.type) {
      candidates.push(value.type)
    }
  }

  return candidates.some(text => IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(text)))
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const environment = import.meta.env.VITE_ENV || 'development'

  // Drop non-actionable noise (e.g. IndexedDB connection lost) before it counts
  // against our Sentry quota.
  if (isIgnoredError(event)) {
    return null
  }

  // Add debug logging in development
  if (environment === 'development') {
    console.log('🔍 Sentry beforeSend:', { // eslint-disable-line no-console
      eventId: event.event_id,
      message: event.message || (event.exception?.values?.[0]?.value),
      tags: event.tags,
      extra: event.extra,
      timestamp: new Date().toISOString(),
    })
  }

  if (event.user) {
    const { email: _email, username: _username, ip_address: _ipAddress, id: _id, ...userWithoutPII } = event.user
    event.user = userWithoutPII
  }

  if (event.request?.url) {
    const url = new URL(event.request.url)
    url.search = ''
    event.request.url = url.toString()
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
      ...breadcrumb,
      data: undefined,
    }))
  }

  return event
}

export function initSentry() {
  const environment = import.meta.env.VITE_ENV || 'development'
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn('Sentry DSN not provided. Sentry will not be initialized.')
    return
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // First line of defense for non-actionable noise. `beforeSend` also filters
    // these patterns to catch nested exceptions / unhandled rejections.
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    beforeSend,
    sendDefaultPii: false,
    // Disable rate limiting in development for testing
    ...(environment === 'development' && {
      // Allow unlimited events in development
      maxBreadcrumbs: 100,
      // Custom transport options to bypass rate limiting
      transportOptions: {
        // Remove default rate limiting
      },
    }),
    release: import.meta.env.VITE_VERSION_NUMBER || __APP_VERSION__ || 'unknown',
    beforeSendTransaction: (transaction) => {
      if (transaction.transaction?.includes('user') || transaction.transaction?.includes('profile')) {
        return null
      }
      return transaction
    },
    initialScope: {
      tags: {
        component: 'hello-screens-client',
      },
    },
  })

  // Override rate limiting for development
  if (environment === 'development') {
    const client = Sentry.getClient()
    if (client) {
      // Override the client's rate limiter for development
      ;(client as any)._rateLimiter = {
        isRateLimited: () => false,
        recordSuccess: () => {},
        recordError: () => {},
      }
      console.log('🚀 Sentry rate limiting disabled for development') // eslint-disable-line no-console
    }
  }

  // eslint-disable-next-line no-console
  console.log(`Sentry initialized for environment: ${environment}`)
}

export function captureException(error: Error, context?: Record<string, any>) {
  const environment = import.meta.env.VITE_ENV || 'development'

  Sentry.withScope((scope) => {
    if (context) {
      const piiKeys = ['email', 'username', 'userid', 'id', 'phone', 'address']

      const sanitizedContext = Object.fromEntries(
        Object.entries(context).filter(([key]) => {
          const isFiltered = piiKeys.includes(key.toLowerCase())
          return !isFiltered
        }),
      )

      scope.setContext('additional', sanitizedContext)
    }

    // In development, bypass deduplication with unique tags
    if (environment === 'development') {
      scope.setTag('dev_timestamp', Date.now().toString())
      scope.setTag('dev_random', Math.random().toString(36).substring(2, 8))
    }

    Sentry.captureException(error)
  })
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (extra) {
      scope.setExtra('details', extra)
    }
    Sentry.captureMessage(message, level)
  })
}
